import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import { sort } from "radash";
import { Fade } from "react-awesome-reveal";
import { Statistic } from "antd";
import { CalendarOutlined, RocketOutlined } from "@ant-design/icons";
import { useLocalStorage } from "react-use";
import { useNavigate } from "react-router-dom";

import { styles } from "@evolved/constants";

import { formatDollars } from "utils/format-dollars";

import { Void } from "components/image-placeholders";
import { useProductOpportunityCountsBy } from "data/use-reports";
import { RangeSelect } from "../range-select";
import { WidgetHeader } from "../widget-header";
import { withSalesRole } from "components/with-role";
import { WidgetContainer } from "../widget-container";
import { convertRange } from "../convert-range";

const { CLICKABLE_STYLE } = styles;

export const filterOption = (input, option) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const AccountsLink = ({ navigate }) => (
  <span onClick={() => navigate("/accounts")} style={CLICKABLE_STYLE}>
    accounts
  </span>
);

const OpportunitiesLink = ({ navigate }) => (
  <span onClick={() => navigate("/opportunities")} style={CLICKABLE_STYLE}>
    opportunities
  </span>
);

const ProductsLink = ({ navigate }) => (
  <span onClick={() => navigate("/products")} style={CLICKABLE_STYLE}>
    products
  </span>
);

export const useProductOpportunitiesState = (organization) => {
  const [isUser, setIsUser] = useLocalStorage(
    `${organization._id}.DASHBOARD.OPPORTUNITY_WIN_RATE.IS_USER`,
    true
  );

  const [sortOrder, setSortOrder] = useLocalStorage(
    `${organization._id}.DASHBOARD.OPPORTUNITY_WIN_RATE.SORT_ORDER`,
    "desc"
  );

  const sortTypes = ["closedRate", "wonValue", "name"];

  const [sortType, setSortType] = useLocalStorage(
    `${organization._id}.DASHBOARD.OPPORTUNITY_WIN_RATE.SORT_TYPE`,
    sortTypes[0]
  );

  const toggleSortType = () => {
    const currentIndex = sortTypes.indexOf(sortType);

    setSortType(sortTypes[(currentIndex + 1) % sortTypes.length]);
  };

  return {
    isUser,
    setIsUser,
    setSortOrder,
    sortOrder,
    sortType,
    toggleSortType,
  };
};

const Component = (props) => {
  const { selectedStateIds } = props;

  const productOpportunityCounts = useProductOpportunityCountsBy(
    "stateId",
    {}
  );

  const navigate = useNavigate();

  const reducedCounts = (productOpportunityCounts ?? []).reduce(
    ({ total, filteredByProduct }, { _id, name, counts }) => {
      const byProduct = { value: 0, name, _id };

      for (const { stateId, count, value } of counts) {
        if (selectedStateIds.includes(stateId)) {
          total.count = total.count + count;
          total.value = total.value + value;
          byProduct.value = byProduct.value + value;
        }
      }

      return {
        total,
        filteredByProduct: [...filteredByProduct, byProduct],
      };
    },
    {
      total: {
        count: 0,
        value: 0,
      },
      filteredByProduct: [],
    }
  );

  const sortedCounts = sort(
    reducedCounts.filteredByProduct,
    (count) => {
      return count.value;
    },
    "desc"
  );

  return (
    <div>
      <div
        style={{ alignItems: "center", display: "flex", marginBottom: "8px" }}
      >
        <div class="mr-auto">
          <WidgetHeader label="Totals" />
        </div>
        <h4
          style={{ fontSize: "18px", marginBottom: "0px", marginLeft: "auto" }}
        >
          All Time
        </h4>
      </div>
      {isEmpty(sortedCounts) ? (
        <Fade>
          <Void
            primary={"No opportunities yet."}
            secondary={
              <>
                Go ahead and create some <ProductsLink navigate={navigate} />,{" "}
                <AccountsLink navigate={navigate} /> and{" "}
                <OpportunitiesLink navigate={navigate} />
              </>
            }
          />
        </Fade>
      ) : (
        <Fade>
          <div
            style={{
              display: "flex",
              marginBottom: "24px",
            }}
          >
            <Statistic
              prefix={<RocketOutlined style={{ marginRight: "4px" }} />}
              value={reducedCounts.total.count}
              style={{ width: "50%" }}
            />
            <Statistic
              value={formatDollars(reducedCounts.total.value)}
            />
          </div>
          <div
            style={{ alignItems: "center", display: "flex", flexWrap: "wrap" }}
          >
            {map(sortedCounts, ({ _id, name, value }, i) => {
              return (
                <div
                  key={_id}
                  style={{
                    ...(i >= 2 ? { marginTop: "8px" } : {}),
                    width: "50%",
                  }}
                >
                  <div style={{ width: "max-content" }}>
                    <div className="flex">
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "black",
                        }}
                      >
                        {name}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          marginLeft: "auto",
                          paddingLeft: "8px",
                        }}
                      >
                        {formatDollars(value)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Fade>
      )}
    </div>
  );
};

export const Totals = withSalesRole((props) => {
  return (
    <WidgetContainer>
      <Component {...props} />
    </WidgetContainer>
  );
});
